import React, {useState} from 'react';
import moment from 'moment';
import {ChevronDownIcon, InformationCircleIcon} from '@heroicons/react/24/outline';

import {useMonthExclusions} from '../../contexts/ExcludedDaysContext';
import {formatHorizon} from '../../utils/excludedDays';

/**
 * One-line notice of the month's excluded (outage) days, which a superuser
 * left out of rankings and payments. Folded by default; opening it lists
 * each day with its horizons and reason. Renders nothing when the month has
 * none. Reads the market's exclusions from ExcludedDaysProvider.
 *
 * @param {{year?: number, month?: number}} props
 */
export default function ExcludedDaysNotice({year, month}) {
  const exclusions = useMonthExclusions(year, month);
  const [open, setOpen] = useState(false);

  if (exclusions.length === 0) return null;

  const groups = groupByDayAndReason(exclusions);
  const dayCount = new Set(exclusions.map((e) => e.target_day)).size;
  // The monthly averages and ranks leave a day out only once the engine has
  // rebuilt the month (hourly); until then, say so.
  const rebuilding = exclusions.some((e) => e.leaderboard_updated === false);

  return (
    <div
      role="status"
      className="mb-4 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 dark:border-brand-800 dark:bg-brand-900/20"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 rounded text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <InformationCircleIcon
          aria-hidden="true"
          className="h-5 w-5 flex-none text-brand-600 dark:text-brand-400"
        />
        <span className="font-semibold text-brand-800 dark:text-brand-200">
          {dayCount} {dayCount === 1 ? 'day' : 'days'} excluded this month
        </span>
        <span className="text-gray-600 dark:text-gray-300">
          · left out of rankings and payments
          {rebuilding ? '; monthly averages update within the hour' : ''}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className={`ml-auto h-4 w-4 flex-none text-brand-600 dark:text-brand-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul className="mt-2 mb-1 ml-7 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
          {groups.map((group) => (
            <li key={`${group.targetDay}|${group.reason}`}>
              <span className="font-mono tabular-nums">
                {moment(group.targetDay, 'YYYY-MM-DD').format('D MMM YYYY')}
              </span>
              {` · ${group.horizonOffsets.map(formatHorizon).join(', ')} · ${group.reason}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One entry per (day, reason) with every horizon excluded for it. */
function groupByDayAndReason(exclusions) {
  const groups = new Map();
  exclusions.forEach((exclusion) => {
    const key = `${exclusion.target_day}|${exclusion.reason}`;
    if (!groups.has(key)) {
      groups.set(key, {
        targetDay: exclusion.target_day, reason: exclusion.reason, horizonOffsets: [],
      });
    }
    groups.get(key).horizonOffsets.push(exclusion.horizon_offset);
  });
  return [...groups.values()].map((group) => ({
    ...group, horizonOffsets: group.horizonOffsets.sort((a, b) => a - b),
  }));
}
